import { useNavigate } from '@tanstack/react-router'
import { CircleUser, LogOut, Monitor, Moon, Sun } from 'lucide-react'

import { useThemeMode, type ThemeMode } from '@/app/theme-mode'
import { useAuth } from '@/auth/use-auth'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const THEME_OPTIONS: { mode: ThemeMode; label: string; icon: typeof Sun }[] = [
  { mode: 'light', label: 'Light', icon: Sun },
  { mode: 'dark', label: 'Dark', icon: Moon },
  { mode: 'system', label: 'Match system', icon: Monitor },
]

export function UserMenu() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [themeMode, setThemeMode] = useThemeMode()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 max-md:px-2" aria-label="Account and appearance">
          <CircleUser />
          {/*
            The name is dropped below `md` rather than truncated: two characters of a name plus an
            ellipsis identify nobody, and the room is worth more to the branch and business date
            beside it. The menu still opens on the person it belongs to.
          */}
          <span className="hidden max-w-40 truncate md:inline">{user?.displayName ?? user?.username}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-64">
        <DropdownMenuLabel>Signed in</DropdownMenuLabel>
        <div className="px-2 pb-2">
          <p className="text-sm font-medium text-heading">{user?.displayName}</p>
          <p className="font-mono text-xs text-muted">{user?.username}</p>
          {user?.roles.length ? (
            <p className="mt-1 text-xs text-muted">{user.roles.join(' · ')}</p>
          ) : null}
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        {THEME_OPTIONS.map(({ mode, label, icon: Icon }) => (
          <DropdownMenuCheckboxItem
            key={mode}
            checked={themeMode === mode}
            onCheckedChange={() => setThemeMode(mode)}
          >
            <Icon className="size-4 text-muted" aria-hidden />
            {label}
          </DropdownMenuCheckboxItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="danger"
          onSelect={() => {
            // Signing out revokes the refresh-token family server-side; dropping the in-memory
            // access token alone would leave a usable cookie behind.
            void signOut().then(() => navigate({ to: '/login' }))
          }}
        >
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
